import { useTranslation } from "react-i18next";
import { OptionGroup } from "@/types/ProductType";
import { AppConfig } from "@/utils/AppConfig";
import { Check } from "lucide-react";
import { useState } from "react";
import { Names } from "./OptionsAndAddonAccordion";

const CashierOption = ({
  option,
  onUpdate,
  error,
}: {
  option: OptionGroup;
  onUpdate: (selected: number[], selectedNames: Names[]) => void;
  error: string;
}) => {
  const { t } = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [selectedOptionsNames, setSelectedOptionsNames] = useState<Names[]>([]);

  const handleSelection = (index
    : number) => {
    let newSelectedOptions: number[] = [];
    let newSelectedNames: Names[] = [];

    if (option.limit === 1) {
      if (selectedOptions.includes(index)) {
        newSelectedOptions = [];
        newSelectedNames = [];
      } else {
        newSelectedOptions = [index];
        newSelectedNames = [
          {
            nameAR: option?.options[index]?.nameAR,
            nameEN: option?.options[index]?.nameEN,
          },
        ];
      }
    } else {
      newSelectedOptions = [...selectedOptions];
      newSelectedNames = [...selectedOptionsNames];

      if (newSelectedOptions.includes(index)) {
        newSelectedOptions = newSelectedOptions.filter((i) => i !== index);
        newSelectedNames = newSelectedNames.filter(
          (_, i) => selectedOptions[i] !== index
        );
      } else if (newSelectedOptions.length < option.limit) {
        newSelectedOptions.push(index);
        newSelectedNames.push({
          nameAR: option?.options[index]?.nameAR,
          nameEN: option?.options[index]?.nameEN,
        });
      }
    }

    setSelectedOptions(newSelectedOptions);
    setSelectedOptionsNames(newSelectedNames);
    onUpdate(newSelectedOptions, newSelectedNames);
  };

  return (
    <div className="my-4">
      <div className="flex justify-between p-2 pb-0">
        <div className="font-bold">{t(option?.nameEN, option?.nameAR)}</div>

        <div className="flex flex-col items-end gap-1">
          <div
            className={`flex h-5 w-fit items-center justify-center rounded-lg px-3 text-sm ${error
                ? "bg-red-200 text-red-500"
                : "bg-[#28c76f1f] text-[#28c76f]"
              }`}
          >
            <span>{t("cashierOptions.required")}</span>
          </div>
          {error && (
            <div className="flex-end flex">
              <p className="self-end text-center text-xs text-red-500">{`${error} ( ${t(
                option?.nameEN,
                option?.nameAR
              )} )`}</p>
            </div>
          )}
        </div>
      </div>
      <div className="p-2 pt-0">
        {t("cashierOptions.selectMax", { count: option.limit })}
      </div>
      <div>
        {option.options.map((item, index) => (
          <label
            key={index}
            className="flex items-center gap-3 px-2 py-1 hover:bg-gray-100"
          >
            <input
              type="checkbox"
              checked={selectedOptions.includes(index)}
              onChange={() => handleSelection(index)}
              className="hidden disabled:cursor-not-allowed"
              disabled={
                option.limit === 1
                  ? false
                  : selectedOptions.length >= option.limit &&
                  !selectedOptions.includes(index)
              }
            />
            <div
              style={{
                cursor:
                  option.limit === 1
                    ? "pointer"
                    : selectedOptions.length >= option.limit &&
                      !selectedOptions.includes(index)
                      ? "not-allowed"
                      : "pointer",
              }}
              className={`flex h-6 w-6 items-center justify-center rounded-sm ${selectedOptions.includes(index) ? "bg-sidebar" : "bg-gray-300"
                }`}
            >
              {selectedOptions.includes(index) && (
                <Check color="white" size={15} strokeWidth={2} />
              )}
            </div>
            <div className="flex w-full justify-between">
              {t(item.nameEN, item.nameAR)}
              {item.price ? (
                <span dir="rtl" className="text-sm text-gray-500">
                  {item.price.toLocaleString("en-EG", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  {AppConfig.currency.icon()}
                </span>
              ) : null}
            </div>
          </label>
        ))}
      </div>
    </div>
  );
};

export default CashierOption;
